// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { ControlsUtils } from '../../UtilsClasses/ControlsUtils';
import TerceraChartRactiveTemplate from '@front/templates/TerceraChartRactiveTemplate.html';
import { Resources } from '@shared/localizations/Resources';
import { Control } from '../Control';
import { ThemeManager } from '../../misc/ThemeManager';
import { KeyEventProcessor } from '@shared/commons/KeyEventProcessor';
import { DynProperty } from '@shared/commons/DynProperty';
import $ from 'jquery';
export class BaseChartRactive extends Control {
    constructor() {
        super();
        this.terceraChart = null;
        this.canvas = null;
        this.context = null;
        this.accumulatedDeltaX = 0;
        this.accumulatedDeltaY = 0;
        this.pinchSensitivity = 0.5;
        this.scrollThreshold = 5;
    }
    createChart() {
        return undefined;
    }
    getType() {
        return 'TerceraChartRactive';
    }
    oninit() {
        BaseChartRactive.staticID++;
        void this.set('id', 'chart' + BaseChartRactive.staticID);
    }
    oncomplete() {
        super.oncomplete();
        KeyEventProcessor.OnKeyDown.Subscribe(this.onGlobalKeyDown, this);
        try {
            this.canvas = $('#' + this.get('id'))[0];
            this.context = this.canvas.getContext('2d', { alpha: true });
            this.terceraChart = this.createChart();
            this.terceraChart.Initialize();
            this.terceraChart.OnUpdateTooltip.Subscribe(this.onUpdateTooltip, this);
            this.terceraChart.ThemeChanged();
            this.observe('width', this.onWidthChanged);
            this.observe('height', this.onHeightChanged);
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log('chart oncomplete exception: ' + ex);
        }
    }
    onteardown() {
        super.onteardown();
        this.terceraChart.OnUpdateTooltip.UnSubscribe(this.onUpdateTooltip, this);
        KeyEventProcessor.OnKeyDown.UnSubscribe(this.onGlobalKeyDown, this);
    }
    onUpdateTooltip(tooltip) {
        showTooltip(null, tooltip);
    }
    onResizeBegin() { }
    onResizeEnd() { }
    onWidthChanged(newWidth) {
        const devicePixelRatio = window.devicePixelRatio || 1;
        this.terceraChart.width = newWidth;
        this.canvas.width = newWidth * devicePixelRatio;
        this.context.scale(devicePixelRatio, devicePixelRatio);
        this.terceraChart.OnResize(this.get('width'), this.get('height'));
    }
    onHeightChanged(newHeight) {
        const devicePixelRatio = window.devicePixelRatio || 1;
        this.terceraChart.height = newHeight;
        this.canvas.height = newHeight * devicePixelRatio;
        this.context.scale(devicePixelRatio, devicePixelRatio);
        this.terceraChart.OnResize(this.get('width'), this.get('height'));
    }
    setSizes() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.rendered) {
                return;
            }
            const panel = this.find('*');
            const w = panel.offsetWidth || BaseChartRactive.ZeroSize;
            const h = panel.offsetHeight || BaseChartRactive.ZeroSize;
            yield this.set({ width: w, height: h });
        });
    }
    resetSizes() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.set({ width: BaseChartRactive.ZeroSize, height: BaseChartRactive.ZeroSize });
        });
    }
    setCursor(cursor) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.set('currentCursor', cursor || 'default');
        });
    }
    onMouseDown(event) {
        super.onMouseDown(event);
        const newChartEvent = this.terceraChart.ImproveArgs(event.original || event);
        this.terceraChart.ProcessMouseDown(newChartEvent);
        ControlsUtils.Capture.SetCapture(this, event.original);
    }
    onMouseMove(event) {
        if (this.captured && event.original) {
            return;
        }
        super.onMouseMove(event);
        const newChartEvent = this.terceraChart.ImproveArgs(event.original || event);
        this.terceraChart.ProcessMouseMove(newChartEvent);
    }
    onMouseUp(event) {
        if (this.captured && event.original) {
            return;
        }
        super.onMouseUp(event);
        const newChartEvent = this.terceraChart.ImproveArgs(event.original || event);
        this.terceraChart.ProcessMouseUp(newChartEvent);
    }
    onMouseWheel(event) {
        super.onMouseWheel(event);
        const correctEvent = event.original || event;
        correctEvent.preventDefault();
        correctEvent.stopPropagation();
        const deltaX = correctEvent.deltaX || 0;
        const deltaY = correctEvent.deltaY || 0;
        this.accumulatedDeltaX += deltaX;
        this.accumulatedDeltaY += deltaY;
        if (Math.abs(this.accumulatedDeltaX) >= this.scrollThreshold ||
            Math.abs(this.accumulatedDeltaY) >= this.scrollThreshold) {
            if (Math.abs(this.accumulatedDeltaX) > Math.abs(this.accumulatedDeltaY)) {
                this.handleHorizontalScroll(correctEvent);
            }
            else {
                this.handleVerticalScroll(correctEvent, deltaY);
            }
            this.accumulatedDeltaX = 0;
            this.accumulatedDeltaY = 0;
        }
    }
    handleHorizontalScroll(correctEvent) {
        const scrollEvent = this.terceraChart.ImproveArgs(correctEvent);
        this.terceraChart.ProcessMouseWheel(scrollEvent);
    }
    handleVerticalScroll(correctEvent, deltaY) {
        if (Math.abs(deltaY) > this.pinchSensitivity) {
            const zoomDelta = deltaY > 0 ? -1 : 1;
            const zoomEvent = { delta: zoomDelta };
            this.terceraChart.ProcessMultiTouches(zoomEvent);
        }
        else {
            const scrollEvent = this.terceraChart.ImproveArgs(correctEvent);
            this.terceraChart.ProcessMouseWheel(scrollEvent);
        }
    }
    onGlobalKeyDown() {
        const container = this.get('parentContainerControl');
        if ((container === null || container === void 0 ? void 0 : container.get('focused')) || this.get('focused')) {
            this.terceraChart.OnKeyDown();
        }
    }
    onMouseEnter(event) {
        super.onMouseEnter(event);
        const newChartEvent = this.terceraChart.ImproveArgs(event.original || event);
        this.terceraChart.ProcessMouseEnter(newChartEvent);
    }
    onMouseLeave(event) {
        super.onMouseLeave(event);
        const newChartEvent = this.terceraChart.ImproveArgs(event.original || event);
        this.terceraChart.ProcessMouseLeave(newChartEvent);
    }
    onDoubleClick(event) {
        super.onDoubleClick(event);
        const newChartEvent = this.terceraChart.ImproveArgs(event.original || event);
        this.terceraChart.ProcessMouseDoubleClick(newChartEvent);
    }
    onClick(event) {
        super.onClick(event);
        const newChartEvent = this.terceraChart.ImproveArgs(event.original || event);
        this.terceraChart.ProcessMouseClick(newChartEvent);
    }
    themeChange() {
        void this.set('backgroundColorBottom', ThemeManager.colors.chartBackground);
        if (!isNullOrUndefined(this.terceraChart)) {
            this.terceraChart.ThemeChanged();
        }
    }
    localize() {
        if (this.terceraChart) {
            this.terceraChart.Localize();
        }
    }
    // #region ICaller
    Properties() {
        const properties = this.terceraChart.Properties();
        // Window colors
        const SeparatorGroup = '#-1#' + Resources.getResource('property.SeparatorGroup.WindowColors');
        /* var prop = new DynProperty("Backgr. top", this.get('backgroundColorTop'), DynProperty.COLOR, DynProperty.VISUAL_GROUP);
    prop.separatorGroup = SeparatorGroup;
    prop.sortIndex = 0;
    properties.push(prop); */
        const prop = new DynProperty('property.Backgr', this.get('backgroundColorBottom'), DynProperty.COLOR, DynProperty.VISUAL_GROUP);
        prop.separatorGroup = SeparatorGroup;
        prop.sortIndex = 1;
        properties.push(prop);
        return properties;
    }
    callBack(properties) {
        this.terceraChart.callBack(properties);
        /* dp = DynProperty.getPropertyByName(properties, "Backgr. top");
    if (dp) this.set('backgroundColorTop', dp.value); */
        const dp = DynProperty.getPropertyByName(properties, 'property.Backgr');
        if (!isNullOrUndefined(dp)) {
            void this.set('backgroundColorBottom', dp.value);
        }
    }
}
BaseChartRactive.staticID = 0;
BaseChartRactive.ZeroSize = 10;
Control.extendWith(BaseChartRactive, {
    template: TerceraChartRactiveTemplate,
    data: function () {
        return {
            backgroundColorBottom: ThemeManager.colors.chartBackground,
            currentCursor: 'default',
            terceraChartPanelContext: null,
            useStyle: true,
            width: 0,
            height: 0
        };
    }
});
